import styles from "../../styles";

/**
 * Component for rendering restart confirmation popup
 */
export class EscapeRestartComponent extends HTMLElement {
    private _onConfirmEscape: (() => void) | null = null;
    private _onCancelEscape: (() => void) | null = null;

    /**
     * Set the confirm handler
     */
    public set onConfirmEscape(handler: () => void) {
        this._onConfirmEscape = handler;
    }

    /**
     * Set the cancel handler
     */
    public set onCancelEscape(handler: () => void) {
        this._onCancelEscape = handler;
    }

    /**
     * Called when the element is connected to the DOM
     */
    public connectedCallback(): void {
        this.render();
        this.setupEventListeners();
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    public disconnectedCallback(): void {
        this.removeEventListeners();
    }

    /**
     * Render the component
     */
    private render(): void {
        const content: string = `
            <style>${styles.popups}</style>
            <div class="popup-overlay">
                <div class="popup">
                    <div class="restart-confirmation">
                        <h3>Congratulations! You have escaped!</h3>
                        <p>Do you want to restart the game or do you want to continue in the current game? 
                        \n
                        Restarting will cause you to lose all your achievements and progress.</p>
                        <div class="restart-buttons">
                            <button id="restart-confirm-button" class="restart-confirm-button">Yes, Restart Game</button>
                            <button id="restart-cancel-button" class="restart-cancel-button">Continue Game</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        this.innerHTML = content;
    }

    /**
     * Setup event listeners
     */
    private setupEventListeners(): void {
        // Confirm button click
        const confirmButton: HTMLElement | null = this.querySelector("#restart-confirm-button");
        if (confirmButton) {
            confirmButton.addEventListener("click", this.handleConfirm.bind(this));
        }

        // Cancel button click
        const cancelButton: HTMLElement | null = this.querySelector("#restart-cancel-button");
        if (cancelButton) {
            cancelButton.addEventListener("click", this.handleCancel.bind(this));
        }

        // Prevent clicks inside popup from closing it
        const popup: HTMLElement | null = this.querySelector(".popup");
        if (popup) {
            popup.addEventListener("click", (e: Event) => e.stopPropagation());
        }
    }

    /**
     * Remove event listeners
     */
    private removeEventListeners(): void {
        const confirmButton: HTMLElement | null = this.querySelector("#restart-confirm-button");
        if (confirmButton) {
            confirmButton.removeEventListener("click", this.handleConfirm.bind(this));
        }

        const cancelButton: HTMLElement | null = this.querySelector("#restart-cancel-button");
        if (cancelButton) {
            cancelButton.removeEventListener("click", this.handleCancel.bind(this));
        }
    }

    /**
     * Handle confirm button click
     */
    private handleConfirm(): void {
        if (this._onConfirmEscape) {
            this._onConfirmEscape();
        }
        this.remove();
    }

    /**
     * Handle cancel button click
     */
    private handleCancel(): void {
        if (this._onCancelEscape) {
            this._onCancelEscape();
        }
        this.remove();
    }
}
